import React, { useState } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import HeaderSpacer from '../Header/HeaderSpacer';

/* ─────────────────────────────────────────────────────────────────────────── */
/*  FAQ Data                                                                    */
/* ─────────────────────────────────────────────────────────────────────────── */

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

interface FaqCategory {
  id: string;
  label: string;
  icon: string;
  items: FaqItem[];
}

const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: 'orders',
    label: 'Buying & Payments',
    icon: 'zmdi-car',
    items: [
      {
        id: 'o1',
        question: 'How do I reserve or buy a car?',
        answer:
          'Browse our inventory, open the listing for the car you\'re interested in, and submit a purchase, reservation, or test drive request with your contact details. No payment is required to submit an inquiry — our sales team will follow up to arrange the next steps.',
      },
      {
        id: 'o2',
        question: 'What payment methods do you accept?',
        answer:
          'We accept cash, bank transfer, and financing arranged through our partner lenders. A deposit secures your reservation on a car, with the balance handled directly with our sales team when you come in to finalise the purchase.',
      },
      {
        id: 'o3',
        question: 'Can I change or cancel my reservation after submitting it?',
        answer:
          'Yes. Reservations can be changed or cancelled any time before a deposit is paid. Once a deposit has been placed, please contact our sales team as soon as possible to discuss changes.',
      },
      {
        id: 'o4',
        question: 'Will I receive a confirmation after submitting an inquiry?',
        answer:
          'Yes. You will receive a confirmation once your request is submitted, and a member of our sales team will contact you directly at the phone number or email you provided to arrange the next steps.',
      },
      {
        id: 'o5',
        question: 'Do you offer financing?',
        answer:
          'Yes, we work with financing partners to help arrange a plan that suits you. Let our sales team know you\'re interested in financing when you reserve a car or visit the dealership, and we\'ll walk you through the options.',
      },
    ],
  },
  {
    id: 'shipping',
    label: 'Delivery & Pickup',
    icon: 'zmdi-truck',
    items: [
      {
        id: 's1',
        question: 'How do I pick up my car?',
        answer:
          'Once your purchase is finalised, you can collect your car directly from our dealership. Our team will confirm the paperwork needed and schedule a pickup time that works for you.',
      },
      {
        id: 's2',
        question: 'Do you offer island-wide delivery?',
        answer:
          'Yes, we offer island-wide delivery for an additional fee that depends on your location. Let our sales team know your delivery address when finalising your purchase for an exact quote.',
      },
      {
        id: 's3',
        question: 'How long does it take to get my car ready?',
        answer:
          'Most vehicles are ready for pickup or delivery within a few business days of finalising the sale, once paperwork, registration transfer, and a final inspection are complete.',
      },
      {
        id: 's4',
        question: 'Can I check the status of my purchase?',
        answer:
          'Yes. Contact our sales team any time with your name or reservation details and they will give you an update on paperwork, registration, or delivery status.',
      },
      {
        id: 's5',
        question: 'Do you offer trade-ins?',
        answer:
          'Yes — tell us about your current car through our Sell Your Car page and we\'ll provide an appraisal. A trade-in value can be applied toward the car you\'re purchasing.',
      },
    ],
  },
  {
    id: 'returns',
    label: 'Deposits & Cancellations',
    icon: 'zmdi-refresh-alt',
    items: [
      {
        id: 'r1',
        question: 'Is my deposit refundable?',
        answer:
          'Deposits are fully refundable if you cancel before the sale is finalised. Contact our sales team with your reservation details and we\'ll process the refund promptly.',
      },
      {
        id: 'r2',
        question: 'How do I cancel a reservation?',
        answer:
          'Contact our sales team with your name and the car you reserved. We\'ll confirm the cancellation and, if a deposit was paid, walk you through the refund process.',
      },
      {
        id: 'r3',
        question: 'How long does a deposit refund take?',
        answer:
          'Once a cancellation is confirmed, refunds are processed within 3–5 business days. The time for funds to appear in your account depends on your bank, typically a few additional business days.',
      },
      {
        id: 'r4',
        question: 'What if the car doesn\'t match the listing or condition described?',
        answer:
          'Every car we list goes through a free vehicle inspection, and any known issues are disclosed upfront. If something doesn\'t match what was described, contact us right away and we\'ll make it right.',
      },
      {
        id: 'r5',
        question: 'Can I get an independent inspection before buying?',
        answer:
          'Absolutely. We\'re happy to arrange a viewing and test drive, and you\'re welcome to bring your own mechanic or inspector before finalising a purchase.',
      },
    ],
  },
  {
    id: 'products',
    label: 'Vehicles & Inventory',
    icon: 'zmdi-tag',
    items: [
      {
        id: 'p1',
        question: 'Are all listed cars currently available?',
        answer:
          'Our inventory is updated in real time. If a car appears in our listings, it is available. Cars that have been sold or reserved are marked accordingly on the listing.',
      },
      {
        id: 'p2',
        question: 'Can I be notified when a specific type of car becomes available?',
        answer:
          'Yes — contact our sales team with what you\'re looking for (make, model, budget) and we\'ll reach out as soon as a matching car arrives in our inventory.',
      },
      {
        id: 'p3',
        question: 'Are the listing photos and details accurate?',
        answer:
          'Yes, every listing uses photos of the actual vehicle along with its real mileage, service history, and condition notes. If you have questions about a specific car, feel free to reach out before visiting.',
      },
      {
        id: 'p4',
        question: 'Do you offer trade-in or fleet purchase pricing?',
        answer:
          'Yes, we offer tailored pricing for fleet or multi-vehicle purchases. Contact our sales team with what you\'re looking for and we\'ll provide a custom quote.',
      },
    ],
  },
  {
    id: 'account',
    label: 'Account & Support',
    icon: 'zmdi-account',
    items: [
      {
        id: 'a1',
        question: 'Do I need an account to submit an inquiry?',
        answer:
          'No, you can submit a purchase, reservation, or test drive request without creating an account — just provide your name and phone number. An account isn\'t required at any point in the process.',
      },
      {
        id: 'a2',
        question: 'How do I contact customer support?',
        answer:
          'You can reach us via the contact form on our website or by emailing our support team directly. We aim to respond to all enquiries within 24 business hours.',
      },
      {
        id: 'a3',
        question: 'What are your customer support hours?',
        answer:
          'Our support team is available Monday to Friday, 9:00 AM – 6:00 PM (local time). We do our best to respond to weekend enquiries on the next business day.',
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Single accordion item                                                       */
/* ─────────────────────────────────────────────────────────────────────────── */

interface AccordionItemProps {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ item, isOpen, onToggle }) => (
  <div className={`faq-item ${isOpen ? 'faq-item--open' : ''}`}>
    <button className="faq-item__q" onClick={onToggle} type="button">
      <span>{item.question}</span>
      <i className={`zmdi zmdi-chevron-down faq-chevron ${isOpen ? 'faq-chevron--up' : ''}`}></i>
    </button>
    {isOpen && (
      <div className="faq-item__a">
        <p>{item.answer}</p>
      </div>
    )}
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Main Page                                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */

const FAQs: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>(FAQ_CATEGORIES[0].id);
  const [openId, setOpenId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const currentCategory = FAQ_CATEGORIES.find((c) => c.id === activeCategory)!;

  // When searching, show results across ALL categories
  const isSearching = searchQuery.trim().length > 0;
  const searchResults: (FaqItem & { categoryLabel: string })[] = isSearching
    ? FAQ_CATEGORIES.flatMap((cat) =>
        cat.items
          .filter(
            (item) =>
              item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
              item.answer.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .map((item) => ({ ...item, categoryLabel: cat.label }))
      )
    : [];

  const totalQuestions = FAQ_CATEGORIES.reduce((t, c) => t + c.items.length, 0);

  return (
    <div>
      <Header />
      <HeaderSpacer />

      {/* Breadcrumb */}
      <div className="container">
        <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
          <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
            Home
            <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true"></i>
          </a>
          <span className="stext-109 cl4">FAQs</span>
        </div>
      </div>

      <section className="bg0 p-t-75 p-b-120">
        <div className="container">

          {/* ── Hero ── */}
          <div className="faq-hero">
            <div className="faq-hero__icon">
              <i className="zmdi zmdi-comment-list"></i>
            </div>
            <h2 className="mtext-111 cl2 p-b-10">Frequently Asked Questions</h2>
            <p className="stext-113 cl6 faq-hero__sub">
              Find answers to the most common questions about our vehicles, reservations, delivery, and more.
            </p>
            <p className="stext-113 cl6" style={{ fontSize: 12, marginTop: 6 }}>
              {FAQ_CATEGORIES.length} categories &nbsp;·&nbsp; {totalQuestions} questions
            </p>

            {/* Search */}
            <div className="faq-search bor8 bg0">
              <i className="zmdi zmdi-search faq-search__icon"></i>
              <input
                className="stext-111 cl8"
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setOpenId(null);
                }}
              />
              {searchQuery && (
                <button className="faq-search__clear" onClick={() => setSearchQuery('')}>
                  <i className="zmdi zmdi-close"></i>
                </button>
              )}
            </div>
          </div>

          {/* ── Search results ── */}
          {isSearching ? (
            <div className="faq-search-results">
              {searchResults.length === 0 ? (
                <div className="faq-empty">
                  <i className="zmdi zmdi-search" style={{ fontSize: 44, color: '#ccc', marginBottom: 14 }}></i>
                  <p className="stext-113 cl6">No results for "<strong>{searchQuery}</strong>"</p>
                  <button className="faq-btn-ghost m-t-15" onClick={() => setSearchQuery('')}>
                    Clear search
                  </button>
                </div>
              ) : (
                <>
                  <p className="stext-111 cl6 p-b-20" style={{ fontSize: 13 }}>
                    {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "<strong>{searchQuery}</strong>"
                  </p>
                  <div className="faq-accordion">
                    {searchResults.map((item) => (
                      <div key={item.id}>
                        <span className="faq-result-category">
                          {item.categoryLabel}
                        </span>
                        <AccordionItem
                          item={item}
                          isOpen={openId === item.id}
                          onToggle={() => setOpenId(openId === item.id ? null : item.id)}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            /* ── Category tabs + accordion ── */
            <div className="faq-layout">

              {/* Sidebar tabs */}
              <nav className="faq-tabs">
                {FAQ_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    className={`faq-tab ${activeCategory === cat.id ? 'faq-tab--active' : ''}`}
                    onClick={() => {
                      setActiveCategory(cat.id);
                      setOpenId(null);
                    }}
                    type="button"
                  >
                    <i className={`zmdi ${cat.icon} faq-tab__icon`}></i>
                    <span className="faq-tab__label">{cat.label}</span>
                    <span className="faq-tab__count">{cat.items.length}</span>
                  </button>
                ))}
              </nav>

              {/* Accordion panel */}
              <div className="faq-panel">
                <h4 className="faq-panel__title">
                  <i className={`zmdi ${currentCategory.icon} m-r-10`} style={{ color: '#717fe0' }}></i>
                  {currentCategory.label}
                </h4>
                <div className="faq-accordion">
                  {currentCategory.items.map((item, i) => (
                    <div
                      key={item.id}
                      style={{ animationDelay: `${i * 50}ms` }}
                      className="faq-accordion__entry"
                    >
                      <AccordionItem
                        item={item}
                        isOpen={openId === item.id}
                        onToggle={() => setOpenId(openId === item.id ? null : item.id)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Still need help ── */}
          {!isSearching && (
            <div className="faq-cta">
              <div className="faq-cta__inner">
                <i className="zmdi zmdi-email" style={{ fontSize: 36, color: '#717fe0', marginBottom: 12, display: 'block' }}></i>
                <h5 className="mtext-110 cl2 p-b-10">Still have a question?</h5>
                <p className="stext-113 cl6 p-b-20">
                  Our support team is happy to help. Reach out and we'll get back to you within 24 hours.
                </p>
                <a
                  href="/contact"
                  className="flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 pointer"
                  style={{ display: 'inline-flex' }}
                >
                  Contact Support
                </a>
              </div>
            </div>
          )}

        </div>
      </section>

      <Footer />
      <BackToTop />

      <style>{`
        /* ── Hero ── */
        .faq-hero {
          text-align: center;
          padding-bottom: 48px;
        }
        .faq-hero__icon {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: #f4f4f9;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          font-size: 30px;
          color: #717fe0;
        }
        .faq-hero__sub {
          max-width: 500px;
          margin: 0 auto 8px;
          line-height: 1.7;
        }

        /* ── Search ── */
        .faq-search {
          display: flex;
          align-items: center;
          max-width: 540px;
          margin: 24px auto 0;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          padding: 0 16px;
          background: #fff;
          box-shadow: 0 2px 10px rgba(0,0,0,0.04);
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .faq-search:focus-within {
          border-color: #717fe0;
          box-shadow: 0 2px 14px rgba(113,127,224,0.12);
        }
        .faq-search__icon {
          color: #aaa;
          font-size: 18px;
          margin-right: 12px;
          flex-shrink: 0;
        }
        .faq-search input {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          padding: 14px 0;
          font-size: 14px;
          color: #444;
          font-family: Poppins-Regular, sans-serif;
        }
        .faq-search__clear {
          background: none;
          border: none;
          cursor: pointer;
          color: #aaa;
          font-size: 16px;
          padding: 4px;
          line-height: 1;
          transition: color 0.2s;
        }
        .faq-search__clear:hover { color: #555; }

        /* ── Layout ── */
        .faq-layout {
          display: grid;
          grid-template-columns: 240px 1fr;
          gap: 32px;
          align-items: start;
        }

        /* ── Sidebar tabs ── */
        .faq-tabs {
          display: flex;
          flex-direction: column;
          gap: 4px;
          position: sticky;
          top: 20px;
        }
        .faq-tab {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          border: 1px solid transparent;
          border-radius: 6px;
          background: none;
          cursor: pointer;
          text-align: left;
          font-family: Poppins-Regular, sans-serif;
          transition: all 0.2s;
          width: 100%;
        }
        .faq-tab:hover {
          background: #f5f5ff;
          border-color: #e0e0f8;
        }
        .faq-tab--active {
          background: #f0f2ff;
          border-color: #c8cef5;
        }
        .faq-tab__icon {
          font-size: 16px;
          color: #aaa;
          transition: color 0.2s;
          flex-shrink: 0;
        }
        .faq-tab--active .faq-tab__icon { color: #717fe0; }
        .faq-tab__label {
          flex: 1;
          font-size: 13px;
          color: #555;
          font-weight: 500;
          transition: color 0.2s;
        }
        .faq-tab--active .faq-tab__label { color: #333; font-weight: 600; }
        .faq-tab__count {
          font-size: 11px;
          background: #e8e8e8;
          color: #888;
          border-radius: 10px;
          padding: 1px 8px;
          transition: all 0.2s;
        }
        .faq-tab--active .faq-tab__count {
          background: #717fe0;
          color: #fff;
        }

        /* ── Panel ── */
        .faq-panel__title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 2px solid #f0f0f0;
          display: flex;
          align-items: center;
        }

        /* ── Accordion ── */
        .faq-accordion {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .faq-accordion__entry {
          animation: faqIn 0.3s ease both;
        }
        @keyframes faqIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .faq-item {
          border: 1px solid #e8e8e8;
          border-radius: 6px;
          overflow: hidden;
          background: #fff;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .faq-item:hover {
          border-color: #c8cef5;
        }
        .faq-item--open {
          border-color: #717fe0;
          box-shadow: 0 2px 12px rgba(113,127,224,0.08);
        }
        .faq-item__q {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding: 16px 20px;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
          font-family: Poppins-Regular, sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: #333;
          line-height: 1.5;
          transition: background 0.2s;
        }
        .faq-item--open .faq-item__q {
          background: #f8f9ff;
          color: #4a55c8;
        }
        .faq-chevron {
          font-size: 18px;
          color: #bbb;
          flex-shrink: 0;
          transition: transform 0.25s ease, color 0.2s;
        }
        .faq-chevron--up {
          transform: rotate(180deg);
          color: #717fe0;
        }
        .faq-item__a {
          padding: 0 20px 18px;
          animation: faqSlide 0.2s ease;
        }
        @keyframes faqSlide {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .faq-item__a p {
          font-size: 13px;
          color: #666;
          line-height: 1.8;
          margin: 0;
        }

        /* ── Search results ── */
        .faq-search-results {
          max-width: 760px;
          margin: 0 auto;
        }
        .faq-result-category {
          display: inline-block;
          font-size: 11px;
          font-weight: 600;
          color: #717fe0;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          margin-bottom: 6px;
          margin-top: 16px;
        }

        /* ── Empty state ── */
        .faq-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 60px 20px;
          text-align: center;
        }
        .faq-btn-ghost {
          background: none;
          border: 1px solid #d0d0d0;
          border-radius: 4px;
          padding: 8px 20px;
          font-size: 12px;
          color: #666;
          cursor: pointer;
          font-family: Poppins-Regular, sans-serif;
          transition: all 0.2s;
        }
        .faq-btn-ghost:hover {
          border-color: #717fe0;
          color: #717fe0;
        }

        /* ── CTA ── */
        .faq-cta {
          margin-top: 60px;
        }
        .faq-cta__inner {
          background: #f8f9ff;
          border: 1px solid #dde2f8;
          border-radius: 8px;
          padding: 40px 30px;
          text-align: center;
          max-width: 480px;
          margin: 0 auto;
        }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .faq-layout {
            grid-template-columns: 1fr;
          }
          .faq-tabs {
            position: static;
            flex-direction: row;
            flex-wrap: wrap;
            gap: 6px;
          }
          .faq-tab {
            flex: 0 0 auto;
            padding: 8px 14px;
          }
          .faq-tab__label {
            font-size: 12px;
          }
        }
        @media (max-width: 480px) {
          .faq-item__q {
            font-size: 13px;
            padding: 14px 16px;
          }
          .faq-item__a {
            padding: 0 16px 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default FAQs;